import { Link, useLocation } from "@tanstack/react-router";
import { Menu, X } from "lucide-react";
import { useState, useEffect } from "react";
import logoWhiteImg from "@/assets/logo-white.svg";

const lightBackgroundLogoFilter =
  "brightness(0) saturate(100%) invert(10%) sepia(48%) saturate(1638%) hue-rotate(173deg) brightness(93%) contrast(103%)";
const DARK_BLUE = "#002239";

export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const cleanPath = location.pathname.replace(/\/$/, "") || "/";
  const isHidden = cleanPath === "/login" || cleanPath === "/reset-password" || cleanPath.startsWith("/admin");

  // Routes with a dark hero background where white text/logo works directly on transparent header.
  // All other routes (light backgrounds) need dark text/logo until the user scrolls.
  const darkHeroRoutes = ["/", "/om-os", "/kontakt", "/biler-til-salg", "/blog"];
  const hasDarkHero =
    darkHeroRoutes.includes(cleanPath) ||
    cleanPath.startsWith("/bilforhandler-");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (isHidden) return null;

  // White text when: scrolled (dark glass bg) OR on a dark-hero route while at top.
  // Dark text when: on a light-background route and not yet scrolled.
  const useWhiteText = scrolled || hasDarkHero;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "shadow-lg border-b border-white/10 backdrop-blur-md backdrop-saturate-150" : ""
      }`}
      style={{
        backgroundColor: scrolled ? "rgba(0,34,57,0.55)" : "transparent",
        WebkitBackdropFilter: scrolled ? "blur(12px) saturate(150%)" : undefined,
      }}
    >
      {/* Top bar */}
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
        <Link to="/" className="flex items-center">
          {/* Explicit width/height eliminates CLS — Lighthouse "Billedelementerne
              har ikke eksplicit width og height" flagged this. logo-white.svg
              has viewBox 2100×900 (ratio 7:3). At display h=50px the natural
              width is 50 × 2100/900 ≈ 117. CSS `h-[50px] w-auto` still owns
              the visual sizing; the attrs only feed the layout aspect ratio. */}
          <img
            src={logoWhiteImg}
            alt="AutoXChange"
            width={117}
            height={50}
            className="h-[50px] w-auto transition-all duration-300"
            style={{
              filter: useWhiteText ? "none" : lightBackgroundLogoFilter,
            }}
          />
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          {[
            { to: "/" as const, label: "Forside", exact: true },
            { to: "/biler-til-salg" as const, label: "Biler til salg" },
            { to: "/kontakt" as const, label: "Kontakt" },
          ].map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="text-sm font-medium transition-colors"
              style={{ color: useWhiteText ? "rgba(255,255,255,0.7)" : DARK_BLUE }}
              activeProps={{
                className: "text-sm font-medium",
                style: { color: useWhiteText ? "var(--color-accent)" : DARK_BLUE },
              }}
              activeOptions={{ exact: item.exact }}
            >
              {item.label}
            </Link>
          ))}
          <Link
            to="/biler-til-salg"
            className="rounded-lg bg-accent px-6 py-2.5 text-sm font-semibold text-accent-foreground transition-all hover:brightness-110"
          >
            Se biler
          </Link>
        </nav>

        <button
          className="md:hidden"
          style={{ color: useWhiteText ? "#fff" : DARK_BLUE }}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          className="border-t border-white/10 px-6 py-6 md:hidden"
          style={{ backgroundColor: "oklch(0.20 0.04 240)" }}
        >
          <nav className="flex flex-col gap-4">
            <Link to="/" className="text-sm font-medium text-white/70 hover:text-accent" onClick={() => setMobileOpen(false)}>Forside</Link>
            <Link to="/biler-til-salg" className="text-sm font-medium text-white/70 hover:text-accent" onClick={() => setMobileOpen(false)}>Biler til salg</Link>
            <Link to="/kontakt" className="text-sm font-medium text-white/70 hover:text-accent" onClick={() => setMobileOpen(false)}>Kontakt</Link>
            <Link to="/biler-til-salg" className="mt-2 rounded-lg bg-accent px-6 py-3 text-center text-sm font-semibold text-accent-foreground" onClick={() => setMobileOpen(false)}>Se biler</Link>
          </nav>
        </div>
      )}
    </header>
  );
}
